import React from 'react';
import * as R from 'ramda';
// components
import { ActionBox } from '../../../components/action-box';
// features
import {
  truckMainFields,
  vendorMainFields,
  driverMainFields,
  makeFilterParams,
  trailerMainFields,
  makeColumnSettings,
} from '../../fleet-list/settings';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// utilities
import endpointsMap from '../../../utilities/endpoints';
// feature fleet-profile/driver
import { TableCell } from '../components/table-cells';
//////////////////////////////////////////////////

const settings = {
  [GC.FIELD_START_DATE]: {
    type: 'date',
    name: 'titles:start-date',
  },
  [GC.FIELD_END_DATE]: {
    type: 'date',
    name: 'titles:end-date',
  },
  [GC.FIELD_DURATION]: {
    searchable: false,
    disableFilter: true,
    name: 'titles:duration',
    customComponent: ({ fieldData }) => {
      if (G.isNilOrEmpty(fieldData)) return null;

      const title = G.getFormattedDurationFromString(fieldData);
      const text = G.getFormattedDurationFromString(fieldData, true);

      return <TableCell text={text} title={title} />;
    },
  },
  [GC.FIELD_CREATED_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:created-date',
  },
  [GC.FIELD_CREATED_BY]: {
    width: 200,
    name: 'titles:created-by',
  },
  [GC.FIELD_LAST_MODIFIED_DATE]: {
    width: 200,
    type: 'date',
    name: 'titles:updated-date',
  },
  [GC.FIELD_LAST_MODIFIED_BY]: {
    width: 150,
    name: 'titles:updated-by',
  },
};

const truckAdditionalColumnSettings = {
  odometerStart: {
    name: 'titles:odometer-start',
  },
  odometerEnd: {
    name: 'titles:odometer-end',
  },
};

const truckAdditionalFilterProps = {
  odometerStart: {
    type: 'number',
    value: 'odometerStart',
    name: G.getWindowLocale('titles:odometer-start', 'Odometer Start'),
  },
  odometerEnd: {
    type: 'number',
    value: 'odometerEnd',
    name: G.getWindowLocale('titles:odometer-end', 'Odometer End'),
  },
};

const filterProps = R.mapObjIndexed((
  { name, options, collection, filterType, disableFilter, type = 'string' },
  value,
) => ({
  value,
  options,
  collection,
  disableFilter,
  type: R.or(filterType, type),
  [GC.FIELD_NAME]: G.ifElse(
    G.isArray(name),
    `${G.getWindowLocale(name[0])}: ${G.getWindowLocale(name[1])}`,
    G.getWindowLocale(name),
  ),
}), settings);

const FleetEntityCustomComponent = ({ field, data, fieldData, callbackData }) => {
  if (R.isNil(fieldData)) return null;

  const { name } = field;

  const { handleGoToProfileByProfileType } = callbackData;

  const fieldsMap = {
    [GC.GRC.DRIVER_LOGIN_ID]: {
      type: 'driver',
      guidKey: GC.GRC.DRIVER_GUID,
    },
    [GC.GRC.FLEET_TRUCK_UNIT_ID]: {
      type: 'truck',
      guidKey: GC.GRC.FLEET_TRUCK_GUID,
    },
    [GC.GRC.FLEET_VENDOR_NAME]: {
      type: 'vendor',
      guidKey: GC.GRC.FLEET_VENDOR_GUID,
    },
    [GC.GRC.FLEET_TRAILER_UNIT_ID]: {
      type: 'trailer',
      guidKey: GC.GRC.FLEET_TRAILER_GUID,
    },
    [GC.GRC.TEAM_DRIVER_LOGIN_ID]: {
      type: 'driver',
      guidKey: GC.GRC.TEAM_DRIVER_GUID,
    },
    [GC.GRC.PRIMARY_DRIVER_LOGIN_ID]: {
      type: 'driver',
      guidKey: GC.GRC.PRIMARY_DRIVER_GUID,
    },
  };

  const { type, guidKey } = R.propOr({}, name, fieldsMap);

  const guid = R.prop(guidKey, data);

  return (
    <ActionBox
      text={fieldData}
      action={() => handleGoToProfileByProfileType({ guid, type })}
    />
  );
};

const getTeamDriverAndPrimaryDriverSettings = () => {
  const historyTypes = ['primaryDriver', 'teamDriver'];

  let entityFilterProps = {};
  let entityColumnSettings = {};

  const filterParams = makeFilterParams(GC.FLEET_DRIVER_REPORT, driverMainFields);
  const columnSettings = makeColumnSettings(GC.FLEET_DRIVER_REPORT, driverMainFields);

  const titleMap = {
    teamDriver: 'titles:team-driver',
    primaryDriver: 'titles:primary-driver',
  };

  historyTypes.forEach((historyType) => {
    const prefixLocale = R.prop(historyType, titleMap);

    entityFilterProps = {
      ...entityFilterProps,
      ...R.compose(
        R.indexBy(R.prop('value')),
        R.map(
          ({ value, name, ...rest }) => ({
            ...rest,
            value: `${historyType}.${value}`,
            name: `${G.getWindowLocale(prefixLocale)}: ${name}`,
          }),
        ),
      )(filterParams),
    };

    const entityFieldIds = [
      GC.GRC.DRIVER_LOGIN_ID,
      GC.GRC.FLEET_VENDOR_NAME,
      GC.GRC.FLEET_TRUCK_UNIT_ID,
      GC.GRC.TEAM_DRIVER_LOGIN_ID,
      GC.GRC.FLEET_TRAILER_UNIT_ID,
      GC.GRC.PRIMARY_DRIVER_LOGIN_ID,
    ];

    entityColumnSettings = {
      ...entityColumnSettings,
      ...R.reduce(
        (acc, item) => {
          let newItem = R.prop(item, columnSettings);

          const key = `${historyType}.${item}`;
          const name = [prefixLocale, R.prop('name', newItem)];

          if (R.includes(key, entityFieldIds)) {
            newItem = R.assoc('customComponent', FleetEntityCustomComponent, newItem);
          }

          return R.assoc(key, { ...newItem, name }, acc);
        },
        {},
        driverMainFields,
      ),
    };
  });

  return {
    filterProps: {
      ...filterProps,
      ...entityFilterProps,
    },
    columnSettings: {
      ...settings,
      ...entityColumnSettings,
    },
  };
};

const getEntityColumnSettingsAndFilterPropsByFleetType = (historyType) => {
  const map = {
    driver: {
      fields: driverMainFields,
      reportType: GC.FLEET_DRIVER_REPORT,
    },
    teamDriver: {
      fields: driverMainFields,
      reportType: GC.FLEET_DRIVER_REPORT,
    },
    truck: {
      fields: truckMainFields,
      reportType: GC.FLEET_TRUCK_REPORT,
    },
    trailer: {
      fields: trailerMainFields,
      reportType: GC.FLEET_TRAILER_REPORT,
    },
    fleetVendor: {
      fields: vendorMainFields,
      reportType: GC.FLEET_VENDOR_REPORT,
    },
  };

  const { fields, reportType } = R.propOr({}, historyType, map);

  const columnSettings = makeColumnSettings(reportType, fields);

  const entityFilterProps = R.compose(
    R.indexBy(R.prop('value')),
    R.map(
      ({ value, ...rest }) => ({ value: `${historyType}.${value}`, ...rest }),
    ),
  )(makeFilterParams(reportType, fields));

  const entityFieldIds = [
    GC.GRC.DRIVER_LOGIN_ID,
    GC.GRC.FLEET_VENDOR_NAME,
    GC.GRC.FLEET_TRUCK_UNIT_ID,
    GC.GRC.TEAM_DRIVER_LOGIN_ID,
    GC.GRC.FLEET_TRAILER_UNIT_ID,
  ];

  const entityColumnSettings = R.reduce(
    (acc, item) => {
      let newItem = R.prop(item, columnSettings);

      const key = `${historyType}.${item}`;

      if (R.includes(key, entityFieldIds)) {
        newItem = R.assoc('customComponent', FleetEntityCustomComponent, newItem);
      }

      return R.assoc(key, newItem, acc);
    },
    {},
    fields,
  );

  return { entityFilterProps, entityColumnSettings };
};

const getColumnSettingsAndFilterProps = (reportType, historyType) => {
  const { entityFilterProps, entityColumnSettings } = getEntityColumnSettingsAndFilterPropsByFleetType(historyType);

  const hasAdditionalFields = R.includes(
    reportType,
    [
      GC.DRIVER_TRUCK_ASSIGNMENT_HISTORY_REPORT,
      GC.TRUCK_DRIVER_ASSIGNMENT_HISTORY_REPORT,
      GC.TRUCK_FLEET_VENDOR_ASSIGNMENT_HISTORY_REPORT,
      GC.FLEET_VENDOR_TRUCK_ASSIGNMENT_HISTORY_REPORT,
    ],
  );

  return {
    columnSettings: {
      ...settings,
      ...hasAdditionalFields ? truckAdditionalColumnSettings : {},
      ...entityColumnSettings,
    },
    filterProps: {
      ...filterProps,
      ...hasAdditionalFields ? truckAdditionalFilterProps : {},
      ...entityFilterProps,
    },
  };
};

const makeRequestPayload = (props) => {
  const { reportType, primaryObjectGuid, primaryObjectGuidKey, primaryObjectBranchGuid } = props;

  const isTeamDriverReport = R.includes(reportType, [GC.DRIVER_TEAM_DRIVER_ASSIGNMENT_HISTORY_REPORT]);

  const systemFieldsMap = {
    DRIVER: [GC.GRC.DRIVER_GUID],
    TRUCK: [GC.GRC.FLEET_TRUCK_GUID],
    VENDOR: [GC.GRC.FLEET_VENDOR_GUID],
    TRAILER: [GC.GRC.FLEET_TRAILER_GUID],
    TEAM_DRIVER: [GC.GRC.TEAM_DRIVER_GUID, GC.GRC.PRIMARY_DRIVER_GUID],
  };

  const type = isTeamDriverReport ? 'TEAM_DRIVER' : R.compose(R.last, R.split('_'), R.dropLast(19))(reportType);
  const systemFields = G.mapIndexed((name, sequence) => ({ name, sequence }), R.propOr([], type, systemFieldsMap));

  return {
    systemFields,
    [GC.CURRENT_BRANCH]: primaryObjectBranchGuid,
    params: {
      [primaryObjectGuidKey]: primaryObjectGuid,
    },
  };
};

const getFormGroupTitleArr = (historyType) => {
  const uiHistoryType = R.equals(historyType, 'fleetVendor') ? 'vendor' : historyType;

  return [`titles:${uiHistoryType}assignment-history`, `${G.toTitleCase(uiHistoryType)} Assignment History`];
};

const makeDefaultSettings = (reportType, historyType) => ({
  ...getColumnSettingsAndFilterProps(reportType, historyType),
  reportType,
  isReport: true,
  actionsPicker: [],
  useSearchableColumns: true,
  withResizableColumns: true,
  useNewTitleFilterInputs: true,
  groupName: `${historyType}AssignmentHistory`,
  additionalTableSettings: { searchableTitles: true },
  formGroupTitleArr: getFormGroupTitleArr(historyType),
  tableCallbackDataProps: ['handleGoToProfileByProfileType'],
  endpoints: {
    list: ({ fleetType }) => {
      const correctFleetType = R.equals(fleetType, 'vendor') ? 'fleetVendor' : fleetType;

      return endpointsMap[`${historyType}AssignmentHistoryByFleetType`](correctFleetType);
    },
  },
  // helpers
  makeRequestPayload,
});

const driverAssignmentHistorySettings = [
  {
    ...makeDefaultSettings(GC.DRIVER_TEAM_DRIVER_ASSIGNMENT_HISTORY_REPORT, 'teamDriver'),
    ...getTeamDriverAndPrimaryDriverSettings(),
    groupName: 'teamDriverAssignmentHistory',
    primaryObjectGuidKey: GC.FIELD_DRIVER_GUID,
    formGroupTitleArr: ['titles:team-driver-assignment-history', 'Team Driver Assignment History'],
    endpoints: {
      list: 'teamDriverAssignmentHistoryFleetDriver',
    },
    resetListAndPaginationProps: [
      'resetTeamDriverAssignmentHistory',
      'handleToggleResetTeamDriverAssignmentHistoryReport',
    ],
  },
  {
    ...makeDefaultSettings(GC.DRIVER_TRUCK_ASSIGNMENT_HISTORY_REPORT, 'truck'),
    primaryObjectGuidKey: GC.FIELD_DRIVER_GUID,
    resetListAndPaginationProps: [
      'resetTruckAssignmentHistory',
      'handleToggleResetTruckAssignmentHistoryReport',
    ],
  },
  {
    ...makeDefaultSettings(GC.DRIVER_TRAILER_ASSIGNMENT_HISTORY_REPORT, 'trailer'),
    primaryObjectGuidKey: GC.FIELD_DRIVER_GUID,
    resetListAndPaginationProps: [
      'resetTrailerAssignmentHistory',
      'handleToggleResetTrailerAssignmentHistoryReport',
    ],
  },
  {
    ...makeDefaultSettings(GC.DRIVER_FLEET_VENDOR_ASSIGNMENT_HISTORY_REPORT, 'fleetVendor'),
    primaryObjectGuidKey: GC.FIELD_DRIVER_GUID,
    resetListAndPaginationProps: [
      'resetVendorAssignmentHistory',
      'handleToggleResetFleetVendorAssignmentHistoryReport',
    ],
  },
];


const truckAssignmentHistorySettings = [
  {
    ...makeDefaultSettings(GC.TRUCK_DRIVER_ASSIGNMENT_HISTORY_REPORT, 'driver'),
    primaryObjectGuidKey: GC.FIELD_TRUCK_GUID,
  },
  {
    ...makeDefaultSettings(GC.TRUCK_FLEET_VENDOR_ASSIGNMENT_HISTORY_REPORT, 'fleetVendor'),
    primaryObjectGuidKey: GC.FIELD_TRUCK_GUID,
  },
];

const trailerAssignmentHistorySettings = [
  {
    ...makeDefaultSettings(GC.TRAILER_DRIVER_ASSIGNMENT_HISTORY_REPORT, 'driver'),
    primaryObjectGuidKey: GC.FIELD_TRAILER_GUID,
  },
  {
    ...makeDefaultSettings(GC.TRAILER_FLEET_VENDOR_ASSIGNMENT_HISTORY_REPORT, 'fleetVendor'),
    primaryObjectGuidKey: GC.FIELD_TRAILER_GUID,
  },
];

const vendorAssignmentHistorySettings = [
  {
    ...makeDefaultSettings(GC.FLEET_VENDOR_DRIVER_ASSIGNMENT_HISTORY_REPORT, 'driver'),
    primaryObjectGuidKey: GC.FIELD_FLEET_VENDOR_GUID,
  },
  {
    ...makeDefaultSettings(GC.FLEET_VENDOR_TRUCK_ASSIGNMENT_HISTORY_REPORT, 'truck'),
    primaryObjectGuidKey: GC.FIELD_FLEET_VENDOR_GUID,
  },
  {
    ...makeDefaultSettings(GC.FLEET_VENDOR_TRAILER_ASSIGNMENT_HISTORY_REPORT, 'trailer'),
    primaryObjectGuidKey: GC.FIELD_FLEET_VENDOR_GUID,
  },
];

const getAssignmentHistoryFilterParams = (reportType, historyType) => {
  const { filterProps } = getColumnSettingsAndFilterProps(reportType, historyType);

  return R.values(filterProps);
};

export {
  truckAssignmentHistorySettings,
  vendorAssignmentHistorySettings,
  driverAssignmentHistorySettings,
  getAssignmentHistoryFilterParams,
  trailerAssignmentHistorySettings,
};
